// CalendarFilterDropdown.tsx — Filter „Kundentermin / Austausch / Sonstige" für
// den Kalender-Neubau (`/termine`, SP5). RdPopover mit Checkboxen, die in den
// CalendarStateContext.filter schreiben (toggleFilter). Die Anwendung auf die
// Events passiert zentral im Context (filteredEvents); block/abwesenheit bleiben
// filterunabhängig sichtbar (D2-Helper in types.ts). Aktive Filterzahl als Badge.

import React from "react";
import { REFINED } from "../risikoanalyseStep/tokens";
import { RdPopover } from "../redesign/table/RdPopover";
import { KONTAKT_EBENE_FARBE, useCalendarState } from "./CalendarStateContext";
import { ART_FARBE } from "./terminBlock";

const ROWS: { key: "kundentermin" | "austausch" | "sonstige"; label: string; color: string }[] = [
    // Farben spiegeln ART_FARBE (terminBlock.ts) — nur visuelle Orientierung.
    { key: "kundentermin", label: "Kundentermine", color: REFINED.gold },
    { key: "austausch", label: "Austausch / Intern", color: REFINED.aub },
    // PO 21.09.2026: weder Kundengespräch noch interne Besprechung.
    { key: "sonstige", label: "Sonstige", color: ART_FARBE.sonstige },
];

const CalendarFilterDropdown: React.FC = () => {
    const { filter, toggleFilter } = useCalendarState();

    // „Aktiv" = nicht beide an (Default = alles sichtbar). Zeigt an, dass etwas
    // ausgeblendet ist. K9: „Nur Termine mit Vorgang" zählt umgekehrt — es ist
    // eine EINSCHRÄNKUNG, also zählt es, wenn es AN ist.
    const hiddenCount =
        ROWS.filter((r) => !filter[r.key]).length + (filter.nurMitVorgang ? 1 : 0);

    return (
        <RdPopover
            width={236}
            trigger={({ toggle, open, ref }) => (
                <button
                    ref={ref}
                    type="button"
                    onClick={toggle}
                    aria-expanded={open}
                    title="Termine filtern"
                    style={triggerStyle(open)}
                >
                    <span>▾ Filter</span>
                    {hiddenCount > 0 && <span style={badgeStyle}>{hiddenCount}</span>}
                </button>
            )}
        >
            {() => (
                <div style={panelStyle}>
                    <div style={headingStyle}>Anzeigen</div>
                    {ROWS.map((r) => {
                        const checked = filter[r.key];
                        return (
                            <label key={r.key} style={rowStyle}>
                                <input
                                    type="checkbox"
                                    checked={checked}
                                    onChange={() => toggleFilter(r.key)}
                                    style={{ width: 16, height: 16, accentColor: REFINED.aub }}
                                />
                                <span
                                    aria-hidden
                                    style={{
                                        width: 10,
                                        height: 10,
                                        borderRadius: 3,
                                        background: r.color,
                                        flex: "0 0 auto",
                                    }}
                                />
                                <span style={{ flex: 1 }}>{r.label}</span>
                            </label>
                        );
                    })}
                    {/* K9: Einschränkung auf Termine, die an einem Vorgang hängen. */}
                    <label style={rowStyle}>
                        <input
                            type="checkbox"
                            checked={filter.nurMitVorgang}
                            onChange={() => toggleFilter("nurMitVorgang")}
                            style={{ width: 16, height: 16, accentColor: REFINED.aub }}
                        />
                        <span
                            aria-hidden
                            style={{
                                width: 10,
                                height: 10,
                                borderRadius: 3,
                                background: REFINED.aub700,
                                flex: "0 0 auto",
                            }}
                        />
                        <span style={{ flex: 1 }}>Nur Termine mit Vorgang</span>
                    </label>
                    <div style={hintStyle}>Pflicht-Blöcke & Abwesenheiten bleiben immer sichtbar.</div>
                    {/* PO 28.09.2026: zuschaltbare Ebene — Termine aus fremden
                        Kalendern, an denen ein Kontakt beteiligt ist, für den
                        ich FO oder BO bin. Nur ansehen (gestrichelter Rahmen). */}
                    <div style={{ ...headingStyle, marginTop: 6 }}>Ebenen</div>
                    <label style={rowStyle} title="Termine aus Kalendern von Kolleginnen und Kollegen, an denen einer Ihrer Kontakte (FO/BO) beteiligt ist">
                        <input
                            type="checkbox"
                            checked={filter.kontaktTermine}
                            onChange={() => toggleFilter("kontaktTermine")}
                            style={{ width: 16, height: 16, accentColor: REFINED.aub }}
                        />
                        <span
                            aria-hidden
                            style={{
                                width: 10,
                                height: 10,
                                borderRadius: 3,
                                border: `1.5px dashed ${KONTAKT_EBENE_FARBE}`,
                                flex: "0 0 auto",
                                boxSizing: "border-box",
                            }}
                        />
                        <span style={{ flex: 1 }}>Termine meiner Kontakte</span>
                    </label>
                    <div style={hintStyle}>
                        Aus fremden Kalendern, nur ansehen — Notizen sind möglich.
                    </div>
                </div>
            )}
        </RdPopover>
    );
};

const triggerStyle = (open: boolean): React.CSSProperties => ({
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    minHeight: 36,
    padding: "0 12px",
    borderRadius: 8,
    border: `1px solid ${open ? REFINED.aub : REFINED.line}`,
    background: REFINED.white,
    color: REFINED.ink,
    font: `600 14px ${REFINED.fontUI}`,
    cursor: "pointer",
    boxShadow: open ? REFINED.focusRing : "none",
});

const badgeStyle: React.CSSProperties = {
    minWidth: 18,
    height: 18,
    padding: "0 5px",
    borderRadius: 9,
    background: REFINED.aub,
    color: REFINED.white,
    font: `700 11px ${REFINED.fontUI}`,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
};

const panelStyle: React.CSSProperties = {
    background: REFINED.white,
    border: `1px solid ${REFINED.line}`,
    borderRadius: 10,
    boxShadow: REFINED.cardShadow,
    padding: 8,
    display: "flex",
    flexDirection: "column",
    gap: 2,
};

const headingStyle: React.CSSProperties = {
    padding: "4px 8px",
    color: REFINED.ink3,
    font: `700 11px ${REFINED.fontUI}`,
    textTransform: "uppercase",
    letterSpacing: 0.4,
};

const rowStyle: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    gap: 10,
    minHeight: 44,
    padding: "0 8px",
    borderRadius: 8,
    cursor: "pointer",
    color: REFINED.ink,
    font: `500 14px ${REFINED.fontUI}`,
};

const hintStyle: React.CSSProperties = {
    padding: "6px 8px 2px",
    color: REFINED.ink3,
    font: `400 11px ${REFINED.fontUI}`,
    lineHeight: 1.4,
};

export default CalendarFilterDropdown;
